import React from "react";
import { message } from "antd";
import { Subscription, PaymentMethod } from "types";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useMutation } from "@apollo/client";

import UPDATE_SUBSCRIPTION_PLAN from "graphql/mutations/UpdateSubscriptionPlan";
import GET_ACTIVE_SUBSCRIPTION from "graphql/queries/GetActiveSubscription";
import useStripeSubscription from "hooks/useStripeSubscription";
import CREATE_ONE_TIME_PAYMENT from "graphql/mutations/CreateOneTimePayment";
import GET_MY_CREDITS from "graphql/queries/GetMyCredits";
import VNPAY_CREATE_PAYMENT from "graphql/mutations/VnpayCreatePayment";

import useErrors from "hooks/useErrors";
import { trackTapfiliateConversion } from "utils/tapfiliate";
import { trackPurchase } from "utils/gtm";
import SubscribeConfirmationModal from "./Subscription/SubscribeConfirmationModal";

type ConfirmModalProps = {
  chosenProgramId?: string;
  chosenProgram?: any;
  // eslint-disable-next-line react/no-unused-prop-types
  disabled: boolean;
  activeSubscription?: Subscription;
  defaultPaymentMethod?: PaymentMethod;
  updateClassTypeSetting: () => void;
  visible: boolean;
  setVisible: (status: boolean) => void;
  onCloseModal: () => void;
  paymentType?: string;
};

const ConfirmModal = ({
  chosenProgramId,
  chosenProgram,
  activeSubscription,
  defaultPaymentMethod,
  updateClassTypeSetting,
  visible,
  setVisible,
  onCloseModal,
  paymentType,
}: ConfirmModalProps) => {
  const { t } = useTranslation("my_credits");
  const { errors, handleError, closeError, closeAllErrors } = useErrors();
  const showModal = () => setVisible(true);
  const closeModal = () => {
    closeAllErrors();
    setVisible(false);
    onCloseModal();
  };
  const elements = useElements();
  const stripe = useStripe();
  // mutations
  const [updateSubscription, { loading: loadingUpdateSub }] = useMutation(
    UPDATE_SUBSCRIPTION_PLAN,
    {
      refetchQueries: [
        {
          query: GET_ACTIVE_SUBSCRIPTION,
        },
      ],
    }
  );
  const [createOneTimePayment, { loading: loadingCreateOne }] = useMutation(
    CREATE_ONE_TIME_PAYMENT,
    {
      refetchQueries: [
        {
          query: GET_MY_CREDITS,
        },
      ],
    }
  );
  const [createVnpayPayment] = useMutation(VNPAY_CREATE_PAYMENT);

  const { createSubscription, loading: loadingCreate } =
    useStripeSubscription();

  const handleBuyPackageProduct = async (
    paymentMethodId: string,
    promotionCode?: string
  ) => {
    try {
      // get ad info from localStorage
      const adInfo = localStorage.getItem("adInfo") || null;
      const {
        data: {
          createOneTimePayment: { invoice },
        },
      } = await createOneTimePayment({
        variables: {
          input: {
            attributes: {
              items: Array.from(
                { length: 1 }, // Because this is package product, we only have one item
                () => chosenProgram.id
              ),
              paymentMethodId,
              promotionCode,
              adInfo,
              amount: chosenProgram.credits,
              expiredAt: moment()
                .add(chosenProgram.packageDuration, "days")
                .format("YYYY-MM-DD HH:mm:ss"),
              creditType: "full_course",
              price: chosenProgram.price,
              currency: chosenProgram.currency,
              stripePriceId: chosenProgram.id,
            },
          },
        },
      });

      const { id, total, customer } = invoice;

      if (invoice.status === "paid") {
        trackTapfiliateConversion(
          id,
          total * 0.01,
          customer,
          chosenProgram.currency
        );
      }

      if (invoice.status !== "paid") {
        const { paymentIntent } = invoice;

        if (paymentIntent.status === "requires_action") {
          if (!stripe || !elements) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
          }

          const result = await stripe.confirmCardPayment(
            paymentIntent.clientSecret
          );
          if (result.error) {
            throw result.error;
          } else if (result?.paymentIntent?.status === "succeeded") {
            trackTapfiliateConversion(
              id,
              total * 0.01,
              customer,
              chosenProgram.currency
            );
          } else {
            throw new Error(
              "Something wrong happened. Your payment cannot be processed."
            );
          }
        }
      }
    } catch (e) {
      handleError(e);
      throw new Error(
        t(
          `Your credit card was declined by the bank. Try again. If it still does not work, add another credit card in "MY BILLING", make it your default payment method and try again.`
        )
      );
    }
  };

  const handleSubscribe = async () => {
    if (!defaultPaymentMethod || !chosenProgramId) {
      message.error(
        "Cannot subscribe without a default payment method. Please go to My Billing to set one."
      );
    } else {
      try {
        // get ad info from localStorage
        const adInfo = localStorage.getItem("adInfo") || null;
        if (chosenProgram.type === "subscription") {
          await createSubscription({
            priceId: chosenProgramId,
            adInfo,
            paymentMethodId: defaultPaymentMethod.id,
          });
          // Track purchase credit for subscription
          trackPurchase(chosenProgram);
          message.info(t("Updated Successfully!"));
        } else if (chosenProgram.type === "package") {
          await handleBuyPackageProduct(defaultPaymentMethod.id, undefined);
          // Track purchase credit for package
          trackPurchase(chosenProgram);
          message.info(t("Purchased Successfully!"));
        }
        closeModal();
      } catch (e) {
        handleError(e);
      }
    }
  };

  const handleClickCreate = () => showModal();
  const handleClickChange = async () => {
    try {
      await updateSubscription({
        variables: {
          input: {
            attributes: {
              priceId: chosenProgramId,
            },
          },
        },
      });
      await updateClassTypeSetting();
      message.info(t("Subscription changed successfully!"));
    } catch (e) {
      handleError(e);
    }
  };

  const handleVnpayCreatePayment = async (): Promise<void> => {
    const amount = chosenProgram.price;
    // Store credits info into VnpayOrder, then create at vnpay_controller
    const orderDesc = {
      productName: chosenProgram.name,
      amount: chosenProgram.credits,
      expiredAt: moment()
        .add(chosenProgram.packageDuration, "days")
        .format("YYYY-MM-DD HH:mm:ss"),
      creditType: "full_course",
      price: chosenProgram.price,
      currency: chosenProgram.currency,
      stripePriceId: chosenProgram.id,
    };
    const returnUrl = `${window.location.origin}/return_url`;

    try {
      const {
        data: {
          vnpayCreatePayment: { url },
        },
      } = await createVnpayPayment({
        variables: {
          input: {
            amount,
            orderDesc,
            returnUrl,
          },
        },
      });

      window.location.href = `${url}`;
    } catch (e) {
      handleError(e);
    }
  };

  return (
    // deleted the yellow button and error as there is no need for one
    <div>
      <SubscribeConfirmationModal
        visible={visible}
        onCloseModal={closeModal}
        onClick={
          activeSubscription && chosenProgram?.type === "subscription"
            ? handleClickChange
            : handleClickCreate
        }
        onConfirm={
          paymentType === "stripe" ? handleSubscribe : handleVnpayCreatePayment
        }
        chosenProgram={chosenProgram}
        loading={loadingCreateOne || loadingCreate || loadingUpdateSub}
        subscribeAgain={!activeSubscription?.renew} // when the current subscription is not renew, it means subscribe again, display different modal.
        renewalDate={activeSubscription?.endDate}
        errors={errors}
        closeError={closeError}
      />
    </div>
  );
};

export default ConfirmModal;
